<!-- Fortinet pages: the vertical navigation becomes the device scope
     (a switcher over "All devices" and every firewall) and the sections
     of whatever is selected. The state comes from the Fortinet page
     through FortinetNavService, and a click here sends it back. -->
<clr-vertical-nav class="fg-nav nav-trigger--bottom" aria-label="Fortinet navigation"
                  [clrVerticalNavCollapsible]="true" [clrVerticalNavCollapsed]="navCollapsed"
                  (clrVerticalNavCollapsedChange)="onCollapsedChange($event)">

  <div class="fg-scope">
    <!-- APG disclosure pattern: the button shows / hides the picker
         (aria-expanded + aria-controls). Not aria-haspopup: that would
         announce a menu, and the picker is a search box and buttons. -->
    <button type="button" class="fg-switch" #switchButton [attr.aria-expanded]="pickerOpen"
            [attr.aria-controls]="pickerId"
            aria-label="Change the device shown" title="Change the device shown"
            (click)="togglePicker(switchButton)">
      <span class="fg-dot" [ngClass]="'sev-' + (nav.fleetMode ? 'all' : nav.deviceSev)"></span>
      <span class="fg-switch-text">
        <span class="fg-switch-title">{{ nav.fleetMode ? 'All devices' : nav.deviceLabel }}</span>
        <span class="fg-switch-sub" *ngIf="nav.deviceSub">{{ nav.deviceSub }}</span>
      </span>
      <cds-icon shape="angle" direction="down" class="fg-switch-caret"></cds-icon>
    </button>

    <!-- device picker. It is placed on the screen (position: fixed, see
         placePicker) and not inside the menu: the menu cuts off whatever is
         wider than itself, and collapsed it is only 48px wide. -->
    <div class="fg-picker" *ngIf="pickerOpen" [id]="pickerId" role="group" aria-label="Device picker"
         [style.top.px]="pickerTop" [style.left.px]="pickerLeft"
         [style.width.px]="pickerWidth" [style.max-height.px]="pickerMaxHeight">
      <input type="search" class="fg-picker-filter"
             placeholder="Name, serial, group" aria-label="Find a device"
             [value]="pickerFilter"
             (input)="setPickerFilter($any($event.target).value)">
      <button type="button" class="fg-item" [class.sel]="nav.fleetMode"
              [attr.aria-current]="nav.fleetMode ? 'true' : null"
              aria-label="Show all devices"
              (click)="selectDevice(fleetId)">
        <span class="fg-dot sev-all"></span>All devices
        <span class="fg-item-count" *ngIf="nav.devices.length">{{ nav.devices.length }}</span>
      </button>
      <div class="fg-picker-tools" *ngIf="pickerHasTree">
        <button type="button" class="fg-tool" aria-label="Expand all groups"
                (click)="setAllGroupsOpen(true)">Expand all</button>
        <button type="button" class="fg-tool" aria-label="Collapse all groups"
                (click)="setAllGroupsOpen(false)">Collapse all</button>
      </div>
      <!-- Each line is a group heading (click: open / close it) or a device.
           What is inside a group is pushed to the right, 14px per level. -->
      <div class="fg-picker-list">
        <ng-container *ngFor="let row of pickerRows; trackBy: trackRow">
          <button type="button" class="fg-group-toggle" *ngIf="row.kind === 'group'"
                  [style.padding-left.px]="4 + row.depth * 14" [attr.aria-expanded]="row.open"
                  [title]="(row.open ? 'Close ' : 'Open ') + (row.groupPath || 'No group')"
                  [attr.aria-label]="(row.open ? 'Close ' : 'Open ') + (row.groupPath || 'No group')"
                  (click)="toggleGroup(row)">
            <cds-icon shape="angle" [attr.direction]="row.open ? 'down' : 'right'" size="12"></cds-icon>
            <span class="fg-group-name">{{ row.label }}</span>
            <!-- a closed group still shows when a device inside needs attention -->
            <span class="fg-dot" *ngIf="row.sev === 'danger' || row.sev === 'offline' || row.sev === 'warning'"
                  [ngClass]="'sev-' + row.sev"></span>
            <span class="fg-item-count">{{ row.count }}</span>
          </button>
          <button type="button" class="fg-item" *ngIf="row.kind === 'device'"
                  [style.padding-left.px]="8 + row.depth * 14"
                  [class.sel]="!nav.fleetMode && row.serial === nav.selectedSerial"
                  [attr.aria-current]="!nav.fleetMode && row.serial === nav.selectedSerial ? 'true' : null"
                  [title]="row.label" [attr.aria-label]="'Show device ' + row.label"
                  (click)="selectDevice(row.serial)">
            <span class="fg-dot" [ngClass]="'sev-' + row.sev"></span>
            <span class="fg-item-text">{{ row.label }}<span class="fg-item-sub" *ngIf="row.sub">{{ row.sub }}</span></span>
          </button>
        </ng-container>
        <div class="fg-picker-empty" *ngIf="!pickerRows.length">No device matches.</div>
      </div>
    </div>
  </div>

  <!-- back to the whole fleet -->
  <a clrVerticalNavLink href="javascript://" class="fg-back" *ngIf="!nav.fleetMode"
     aria-label="Back to all devices"
     (click)="selectDevice(fleetId)">
    <cds-icon shape="arrow" direction="left" clrVerticalNavIcon></cds-icon>
    All devices
  </a>

  <div class="fg-cap" *ngIf="sections.length">{{ nav.fleetMode ? 'Fleet' : 'This device' }}</div>
  <a clrVerticalNavLink href="javascript://" *ngFor="let section of sections; trackBy: trackSection"
     [class.active]="section.id === nav.current" [class.fg-off]="!!section.off"
     [attr.aria-current]="section.id === nav.current ? 'page' : null"
     [attr.aria-disabled]="section.off ? true : null" [title]="section.off || section.label"
     [attr.aria-label]="section.badge ? section.label + ', ' + section.badge : section.label"
     (click)="selectSection(section)">
    <cds-icon [attr.shape]="section.icon" clrVerticalNavIcon></cds-icon>
    <span class="fg-link-body">
      <span class="fg-link-label">{{ section.label }}</span>
      <span class="fg-badge" *ngIf="section.badge" [class.bad]="section.badgeBad">{{ section.badge }}</span>
    </span>
  </a>

  <!-- groups of the fleet page (config.yaml `groups:`), drawn as a tree: a sub-group is pushed
       to the right (icon included), with a guide line under each group above it (see the CSS) -->
  <!-- Collapsed menu: a column of identical tag icons says nothing, so the
       groups become ONE entry. It is highlighted while the page is focused on
       a group, its tooltip names that group, and a click opens the menu so
       the groups can be read. -->
  <a clrVerticalNavLink href="javascript://" class="fg-groups-folded"
     *ngIf="navCollapsed && nav.fleetMode && nav.groups.length > 1"
     [class.active]="nav.groupFilter !== null"
     [title]="foldedGroupsText" [attr.aria-label]="foldedGroupsText"
     (click)="expandMenu()">
    <cds-icon shape="tag" clrVerticalNavIcon [attr.solid]="nav.groupFilter !== null ? true : null"></cds-icon>
    Groups
  </a>

  <ng-container *ngIf="!navCollapsed && nav.fleetMode && nav.groups.length > 1">
    <div class="fg-cap">Groups</div>
    <a clrVerticalNavLink href="javascript://" *ngFor="let group of nav.groups; trackBy: trackGroup"
       class="fg-group-link" [style.--fg-depth]="group.depth"
       [class.active]="nav.groupFilter === group.name"
       [attr.aria-current]="nav.groupFilter === group.name ? 'true' : null"
       [title]="'Focus the page on ' + (group.name || 'the devices without a group')"
       [attr.aria-label]="'Focus the page on ' + (group.name || 'the devices without a group')"
       (click)="selectGroup(group.name)">
      <cds-icon shape="tag" clrVerticalNavIcon></cds-icon>
      <span class="fg-link-body">
        <span class="fg-link-label">{{ group.label }}</span>
        <span class="fg-badge">{{ group.devices }}</span>
      </span>
    </a>
  </ng-container>
</clr-vertical-nav>
